import { createDeserializer } from "@jsonapi-serde/client";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { z } from "zod";
import {
    caseDisbursementSchemaInput,
    caseFormSchemaInput,
    caseIntakeSchemaInput,
} from "@/components/Forms/schema/caseFormSchemaInput.ts";
import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl } from "@/utils/api.ts";

const resourceSelector = createDeserializer({
    type: "case",
    cardinality: "one",
    attributesSchema: caseFormSchemaInput,
    relationships: {
        idPeople: {
            type: "person",
            cardinality: "one_nullable",
            included: {
                attributesSchema: z.object({
                    nameFirst: z.string().nullish(),
                    nameLast: z.string().nullish(),
                }),
            },
        },
        intake: {
            type: "caseIntake",
            cardinality: "one_nullable",
            included: {
                attributesSchema: caseIntakeSchemaInput,
            },
        },
        disbursement: {
            type: "caseDisbursement",
            cardinality: "one_nullable",
            included: {
                attributesSchema: caseDisbursementSchemaInput,
            },
        },
    },
});

export type Case = ReturnType<typeof resourceSelector>;

type Props = {
    isQueryEnabled: boolean;
    caseUUID: string;
};
export const useFetchCaseByUUID = ({ isQueryEnabled, caseUUID }: Props): UseQueryResult<Case> => {
    const fetch = useAuthenticatedFetch();

    return useQuery({
        enabled: isQueryEnabled,
        queryKey: ["case-uuid", { caseUUID }],
        queryFn: async ({ signal }) => {
            const url = apiUrl(`/case/by-uuid/${caseUUID}`);

            const response = await fetch(url, {
                signal,
                headers: {
                    accept: "application/vnd.api+json",
                },
            });

            return await response.json();
        },
        select: resourceSelector,
    });
};
